import React, { useContext, useEffect, useState } from "react";
import {
  makeStyles,
  useTheme,
  Container,
  CssBaseline,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Hidden,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { compose } from "recompose";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import logo from "../../../contents/img/Rampver_Online_Logo-main.png";
import logo2 from "../../../contents/img/Rampver Online Logo Square.png";
import * as CONSTANTS from "./constants";
import AdditionalInformation from "./additionalInformation";
import PEPDeclaration from "./PEPDeclaration";
import * as ROUTES from "../../../constants/route";
import { AuthUserContext, withAuthorization } from "../../Session";
import { withFirebase } from "../../Firebase";
import Functions from "./functions";
import BasicKYCHandler from "../../GenericPages/basicKYCHandler";
import FATCA from "./FATCA";
import Summary from "./summary";
import SubmissionSuccessContent from "./SubmissionSuccessContent";
import { VideoAttestationStep } from "../../VideoAttestation";
import { assignStatus } from "../../../services/metrics";
import { getKycSubmissionErrorMessage } from "../errorMessage";
import SimpleModal from "../../Modal";
import { useMaterializedUploads } from "../useMaterializedUploads";
import {
  AttestationStatusNotice,
  isAttestationEligible,
  useAttestationEligibility,
} from "../../VideoAttestation/useAttestationEligibility";


const drawerWidth = 350;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    [theme.breakpoints.up("sm")]: {
      marginLeft: drawerWidth,
    },
    [theme.breakpoints.down("sm")]: {
      backgroundColor: "#256141",
    },
  },

  menuButton: {
    marginRight: theme.spacing(2),
  },

  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: "#256141",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    fontFamily: "Gill Sans, Poppins",
    background: "white",
    height: "100%",
    width: "100%",
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    overflow: "hidden",
    position: "relative",
  },

  navBar: {
    backgroundColor: "#fff",
  },
  brand: {
    maxWidth: "140px",
    margin: "10px auto 13px auto",
  },
  brand2: {
    maxWidth: "140px",
    height: "60px",
    margin: "5px 0 5px 0",
  },


}));

const LongKYCPage = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const smBreakpoint = useMediaQuery(theme.breakpoints.up("sm"));
  const context = useContext(AuthUserContext);
  const account = useAttestationEligibility(props.firebase, context.uid, "long-kyc");
  const { refresh: refreshAccount } = account;
  const [entryError, setEntryError] = useState("");
  const [entryRetry, setEntryRetry] = useState(0);
  const [entryHydrated, setEntryHydrated] = useState(false);

  const [additionalInfo, setAdditionalInfo] = useState(
    CONSTANTS.INITIAL_STATE.additionalInfo
  );
  const [uploadImg, setUploadImg] = useState(CONSTANTS.INITIAL_STATE.images);
  const [pepDeclaration, setPEPDeclaration] = useState(
    CONSTANTS.INITIAL_STATE.PEPDeclaration
  );
  const [fatca, setFATCA] = useState(
    CONSTANTS.INITIAL_STATE.FATCA
  );
  const [signature, setSignature] = useState(
    CONSTANTS.INITIAL_STATE.signature
  );
  const [modalAlert, setModalAlert] = useState(
    CONSTANTS.INITIAL_STATE.modalAlert
  );
  const [validation, setValidation] = useState({
    loading: false,
    openDrawer: false,
  });
  const [step, setStep] = useState({
    step: 1,
    fixStep: 1,
  });
  const [initialLoad, setInitialLoad] = useState(true);
  const {
    handleDropzone,
    handleDropzoneDelete,
    isMaterializingUpload,
  } = useMaterializedUploads(setUploadImg, setModalAlert);
  const functions = new Functions();

  useEffect(() => {
     let active = true;
     const userData = JSON.parse(window.localStorage.getItem("additionalInfo"));
     const bgcolor = "#fff";
     const bodyElt = document.querySelector("body");
     bodyElt.style.backgroundColor = bgcolor;
     setInitialLoad(true);

     const uid = context.uid;

     const localUserData = userData !== null && {
       primaryOccupation: true,
     };

     const usersRef = props.firebase.db.collection("users");
     const kycRef = usersRef.doc(uid).collection("KYC").doc("KYC");

     const load = async () => {
       setEntryError("");
       try {
         let data = await refreshAccount();
         if (!active) return;
         if (!data) throw new Error("Account status unavailable");
         // Only the existing supported form-entry transition may assign LONG_KYC-1.
         if (Number(data?.stats?.tier) !== 3 && ["SHORT_KYC-5", "LIMIT"].includes(data?.stats?.status)) {
           const result = await assignStatus({ uid, status: "LONG_KYC-1" });
           if (result.error?.response?.status === 409 &&
               result.error.response.data?.code === "invalid-status-transition") {
             await refreshAccount();
             if (active) setInitialLoad(false);
             return;
           }
           if (result.error || result.data?.ok !== true) {
             throw new Error("We couldn't start your application. Please retry.");
           }
           data = await refreshAccount();
         }
         if (!active) return;
         if (!isAttestationEligible(data?.stats, "long-kyc")) {
           setInitialLoad(false);
           return;
         }
         const kycSnapshot = await kycRef.get({ source: "server" });
         if (!active) return;
         const kycData = kycSnapshot.data();
         buildData(userData !== null ? localUserData : data);
         setEntryHydrated(true);
         if (kycData) {
           setAdditionalInfo((prevState) => ({
             ...prevState,
             currentAdd:kycData.currentAddress,
              isPrimaryId: kycData.ids?.isPrimaryId,
             existingID: !kycData.ids?.TIN && !kycData.ids?.SSS,
             ids: {
               ...prevState.ids,
               idValidity: kycData.ids?.idValidity || "",
               hasExpiration: kycData.ids?.hasExpiration !== false,
             },
           }));
         }
       } catch {
         if (active) {
           setEntryError("We couldn't load your application. Please retry before continuing.");
           setInitialLoad(false);
         }
       }
     };
     load();
     return () => { active = false; };
   // Draft hydration runs only on entry/retry, never on live status updates.
   }, [entryRetry, refreshAccount]);

  useEffect(() => {

    if (
      additionalInfo.work?.primaryOccupation !== "" &&
      modalAlert.modalOpen === false
    ) {
      window.localStorage.setItem(
        "additionalInfo",
        JSON.stringify(additionalInfo)
      );
      window.localStorage.setItem(
        "PEPDeclaration",
        JSON.stringify(pepDeclaration)
      );
      window.localStorage.setItem("FATCA", JSON.stringify(fatca));
    }
  }, [additionalInfo, pepDeclaration,fatca]);

  const buildData = (datas) => {
    let additionalData;
    let pepDeclarationData;
    let fatcaData;
    if (datas.primaryOccupation) {
      const additionalInfo = JSON.parse(window.localStorage.getItem("additionalInfo"));
      const pepDeclaration = JSON.parse(
        window.localStorage.getItem("PEPDeclaration")
      );
      const fatca = JSON.parse(window.localStorage.getItem("FATCA"));

      additionalData = { ...additionalInfo };
      pepDeclarationData = {...pepDeclaration};
      fatcaData = { ...fatca };
    } else{
      additionalData={...additionalInfo}
      pepDeclarationData = { ...pepDeclaration };
      fatcaData = { ...fatca };
    }

    setValidation((prevState) => ({ ...prevState, openDrawer: false }));
    setAdditionalInfo(additionalData);
    setPEPDeclaration(pepDeclarationData);
    setFATCA(fatcaData);
    setInitialLoad(false);
  
  };

  const nextStep = () => {
    if (isMaterializingUpload) return;
    window.scrollTo(0, 0);

    setStep((prevState) => ({
      ...prevState,
      step: prevState.step + 1,
    }));
  };

  const fixedStep = (value) => {
    window.scrollTo(0, 0);

    setStep((prevState) => ({
      ...prevState,
      fixStep: prevState.fixStep === 5 ? 5 : value,
    }));
  };
  const prevStep = () => {
    window.scrollTo(0, 0);
    setStep((prevState) => ({
      ...prevState,
      step: prevState.step - 1,
    }));
  };

  const handleChangeAdditionalInfo = (value) => {
    setAdditionalInfo((prevState) => ({
      ...prevState,
      ids: {
        ...prevState.ids,
        idType: value.TIN 
  ? "TIN" 
  : value.SSS 
    ? "SSS" 
    : "TIN", 
        SSS: value.SSS,
        TIN: value.TIN,
      },
      businessAddress: {
        barangay:value.barangay,
        street: value.street,
        city: value.cityAddress,
        province: value.provinceAddress,
        zip: value.zip,
        country: value.countryAddress,
      },
      permanentAddress: {
        barangay:value.pebarangay,
        street: value.pestreet,
        city: value.pecityAddress,
        province: value.peprovinceAddress,
        zip: value.pezip,
        country: value.pecountryAddress,
      },
      work: {
        primaryOccupation: value.primaryoccupation,
        nameOfEmployer: value.nameOfEmployer,
        businessNature: value.businessNature,
        annualIncomeAmount: value.annualIncomeAmount,
      },
    }));
  };

   const handleChangePEPDec = (value) => {
     if (value.governmentRelative === "0" && value.governmentWork === "0") {
             setPEPDeclaration({
               governmentWork: value.governmentWork,
               governmentRelative: value.governmentRelative,
               banks: "",
             });
       return setUploadImg((prevState) => ({
         ...prevState,
         idPEPDeclaration: null,
         idPEPDeclarationLabel: "",
         idPEPDeclarationPreview: null,
       }));
     } else {
       setPEPDeclaration({
         governmentWork: value.governmentWork,
         governmentRelative: value.governmentRelative,
         banks: value.banks,
       });
     }
   };
   const handleChangeFATCA = (value) => {
    if (value.usPerson === "0"){
      return setFATCA({
        usPerson: value.usPerson,
        usCitizen: "",
        usResident: "",
        usAlien: "",
        usPhone: "",
        usBorn: "",
        usAddressResidence: "",
        usAddressMailing: "",
        usAccount: "",
        usPOA: "",
        usCareOfAddress: "",
      });
    }
      setFATCA({
        usPerson: value.usPerson,
        usCitizen: value.usCitizen,
        usResident: value.usResident,
        usAlien: value.usAlien,
        usPhone: value.usPhone,
        usBorn: value.usBorn,
        usAddressResidence: value.usAddressResidence,
        usAddressMailing: value.usAddressMailing,
        usAccount: value.usAccount,
        usPOA: value.usPOA,
        usCareOfAddress: value.usCareOfAddress,
      });
     
   };
  
   const saveAndContinue = async () => {
     if (validation.loading) return;
     setValidation((prevState) => ({ ...prevState, loading: true }));
     const current = await refreshAccount();
     if (!isAttestationEligible(current?.stats, "long-kyc")) {
       setValidation((prevState) => ({ ...prevState, loading: false }));
       return;
     }
    
     let requiredFields = [
       { field: additionalInfo.businessAddress.barangay, name: "Business Barangay" },
       { field: additionalInfo.businessAddress.street, name: "Business Street" },
       { field: additionalInfo.businessAddress.city, name: "Business City" },
       { field: additionalInfo.businessAddress.province, name: "Business Province" },
       { field: additionalInfo.businessAddress.zip || "n/a", name: "Business ZIP" },
       { field: additionalInfo.businessAddress.country, name: "Business Country" },

       { field: additionalInfo.permanentAddress.barangay, name: "Permanent Barangay" },
       { field: additionalInfo.permanentAddress.street, name: "Permanent Street" },
       { field: additionalInfo.permanentAddress.city, name: "Permanent City" },
       { field: additionalInfo.permanentAddress.province, name: "Permanent Province" },
       { field: additionalInfo.permanentAddress.zip || "n/a", name: "Permanent ZIP" },
       { field: additionalInfo.permanentAddress.country, name: "Permanent Country" },
       {
         field: additionalInfo.work.primaryOccupation,
         name: "Primary Occupation",
       },
       { field: additionalInfo.work.nameOfEmployer, name: "Name of Employer" },
       {
         field: additionalInfo.work.businessNature,
         name: "Nature of Business",
       },
       { field: additionalInfo.work.annualIncomeAmount, name: "Annual Income" },
       { field: additionalInfo.ids.idType, name: "ID Type" },
     ];
      // if (additionalInfo.existingID && additionalInfo.ids.idType ==="SSS") {
      //   requiredFields = requiredFields.concat([
      //     {
      //       field:
      //         additionalInfo.ids.SSS === undefined
      //           ? ""
      //           : additionalInfo.ids.SSS,
      //       name: "SSS",
      //     },
        
      //   ]);
      // } 
      // if (additionalInfo.existingID && additionalInfo.ids.idType === "TIN") {
      //   requiredFields = requiredFields.concat([
        
      //     {
      //       field:
      //         additionalInfo.ids.TIN === undefined
      //           ? ""
      //           : additionalInfo.ids.TIN,
      //       name: "TIN",
      //     },
      //   ]);
      // } 
      if (fatca.usPerson === "1") {
        requiredFields = requiredFields.concat([
          {
            field: fatca.usPerson === undefined ? "" : fatca.usPerson,
            name: "US Person",
          },
          {
            field: fatca.usCitizen === undefined ? "" : fatca.usCitizen,
            name: "US Citizen",
          },
          {
            field: fatca.usResident === undefined ? "" : fatca.usResident,
            name: "US Resident",
          },
          {
            field: fatca.usAlien === undefined ? "" : fatca.usAlien,
            name: "US Resident Alien",
          },
          {
            field: fatca.usPhone === undefined ? "" : fatca.usPhone,
            name: "US Telephone Number",
          },
          {
            field: fatca.usBorn === undefined ? "" : fatca.usBorn,
            name: "Born in US",
          },
          {
            field:
              fatca.usAddressResidence === undefined
                ? ""
                : fatca.usAddressResidence,
            name: "US Residence Address",
          },
          {
            field:
              fatca.usAddressMailing === undefined
                ? ""
                : fatca.usAddressMailing,
            name: "US Mailing Address",
          },
          {
            field: fatca.usAccount === undefined ? "" : fatca.usAccount,
            name: "US Account",
          },
          {
            field: fatca.usPOA === undefined ? "" : fatca.usPOA,
            name: "US POA",
          },
          {
            field:
              fatca.usCareOfAddress === undefined ? "" : fatca.usCareOfAddress,
            name: "US Care Of Address",
          },
        ]);
      } 

      if (
        (pepDeclaration.governmentWork === "1" ||
        pepDeclaration.governmentRelative === "1")
      ) {
        requiredFields = requiredFields.concat([
          { field: pepDeclaration.governmentWork, name: "Government Work" },
          {
            field: pepDeclaration.governmentRelative,
            name: "Government Relative",
          },
          { field: pepDeclaration.banks, name: "Banks" },
        ]);
      } 

      for (const { field, name } of requiredFields) {
        if (field === "" || field === null) {
          setValidation((prevState) => ({
            ...prevState,
            loading: false,
          }));
          setModalAlert((prevState) => ({
            ...prevState,
            modalOpen: true,
            modalTitle: `The ${name} field is empty. Please complete the form!`,
            modalSuccess: false,
          }));
          return false;
        } 
      }
   
    if (
      (pepDeclaration.governmentWork === "1" ||
        pepDeclaration.governmentRelative === "1") &&
      uploadImg.idPEPDeclaration === null
    ) {
      setValidation((prevState) => ({
        ...prevState,
        loading: false,
      }));
      setModalAlert((prevState) => ({
        ...prevState,
        modalOpen: true,
        modalTitle: "Please upload your PEP Declaration.",
        modalSuccess: false,
      }));
      return false;
    }
     if (
       (additionalInfo.isPrimaryId === undefined ||
       additionalInfo.isPrimaryId === false) &&
       (  !uploadImg.idPhotoFront  ||
         !uploadImg.idPhotoBack )
     ) {
       setValidation((prevState) => ({
         ...prevState,
         loading: false,
       }));
       setModalAlert((prevState) => ({
         ...prevState,
         modalOpen: true,
         modalTitle: "Please upload your ID.",
         modalSuccess: false,
       }));
     }
     else{
       functions
         .doAddLongKYC(
           context.uid,
           uploadImg,
           additionalInfo,
           pepDeclaration,
           signature,
           fatca
         )
          .then(async (res) => {
           if (!res.data.ok) throw new Error(res.data.message);
           setValidation((prevState) => ({
             ...prevState,
             loading: false,
           }));
            setModalAlert((prevState) => ({
             ...prevState,
             modalOpen: true,
             modalTitle: "Your Client Application was successfully submitted!",
             modalContent: <SubmissionSuccessContent />,
             modalSuccess: true,
            }));
            await refreshAccount();
          })
          .catch(async (err) => {
            if ([403, 409].includes(err.response?.status)) await refreshAccount();
           const redirectData = err.response?.data;
           if (redirectData?.redirect) {
             setValidation((prevState) => ({ ...prevState, loading: false }));
             setModalAlert((prevState) => ({
               ...prevState,
               modalOpen: true,
               modalTitle: redirectData.message,
               modalContent: "",
               modalSuccess: false,
             }));
             setTimeout(() => { window.location.href = "/dashboard"; }, 3000);
             return;
           }
            const errMsg = getKycSubmissionErrorMessage(err);
           setValidation((prevState) => ({
             ...prevState,
             loading: false,
           }));
           setModalAlert((prevState) => ({
             ...prevState,
             modalOpen: true,
             modalTitle: "Uh oh, an error has occurred!",
             modalContent: errMsg,
             modalSuccess: false,
           }));
         });
 

     }
    
  };


  const handleDrawerOpen = () => {
    setValidation((prevState) => ({
      ...prevState,
      openDrawer: !prevState.openDrawer,
    }));
  };


  const handleModalClose = () => {
    setModalAlert((prevState) => ({ ...prevState, modalOpen: false }));
    if (modalAlert.modalSuccess) {
      props.history.push(ROUTES.DASHBOARD);
    }
  };

   const handleSignature = (url) => {
     setSignature({signatureUrl:url})
    };

   const handleEraseCurrentSig = () => {
    setSignature({ signatureUrl: null });
    };

  const renderSwitch = (step) => {
    if (initialLoad && !entryError && !account.error) {
      return <p role="status">Loading your application...</p>;
    }
    if ((!account.eligible || entryError || !entryHydrated) && !modalAlert.modalSuccess) {
      return (
        <AttestationStatusNotice
          account={{ ...account, error: entryError || account.error || (account.eligible && !entryHydrated
            ? "Please refresh to load your application before continuing."
            : "") }}
          history={props.history}
          onRetry={() => {
            if (entryError || !entryHydrated) setEntryRetry((value) => value + 1);
            else refreshAccount();
          }}
        />
      );
    }
    switch (step) {
      case 1:
        return (
           <AdditionalInformation
            additionalInfo={additionalInfo}
            uploadImg={uploadImg}
            editKYC={false}
            handleDropzone={handleDropzone}
            handleDropzoneDelete={handleDropzoneDelete}
            nextStep={nextStep}
            fixedStep={fixedStep}
            smBreakpoint={smBreakpoint}
            handleChangeData={handleChangeAdditionalInfo}
            initialLoad={initialLoad}
            firebase={props.firebase}
            isMaterializingUpload={isMaterializingUpload}
          />
        );
      case 2:
        return (
          <PEPDeclaration
            PEPData={pepDeclaration}
            uploadImg={uploadImg}
            prevStep={prevStep}
            editKYC={false}
            handleDropzone={handleDropzone}
            handleDropzoneDelete={handleDropzoneDelete}
            nextStep={nextStep}
            fixedStep={fixedStep}
            handleChangePEPDec={handleChangePEPDec}
            initialLoad={initialLoad}
            isMaterializingUpload={isMaterializingUpload}
          />
        );
      case 3:
        return (
          <FATCA
            FATCAData={fatca}
            prevStep={prevStep}
            editKYC={false}
            nextStep={nextStep}
            fixedStep={fixedStep}
            handleChangeFATCA={handleChangeFATCA}
            initialLoad={initialLoad}
          />
        );
      case 4:
        return (
          <VideoAttestationStep
            prevStep={prevStep}
            nextStep={nextStep}
            fixedStep={fixedStep}
            embedded={true}
            firebase={props.firebase}
          />
        );
      case 5:
        return (
          <Summary
            modalAlert={modalAlert}
            loading={validation}
            additionalInfo={additionalInfo}
            PEPData={pepDeclaration}
            FATCAData={fatca}
            signature={signature}
            prevStep={prevStep}
            handleSignature={handleSignature}
            handleEraseCurrentSig={handleEraseCurrentSig}
            saveAndContinue={saveAndContinue}
            handleModalClose={handleModalClose}
            isUpdateKYC={true}
          />
        );

      default:
        return <BasicKYCHandler history={props.history} />;
    }
  };

  const drawer = (
    <>
      <div className={classes.drawerHeader}>
        <img className={classes.brand} src={logo2} alt="rampver-logo"></img>

        {!validation.openDrawer ? (
          ""
        ) : (
          <IconButton onClick={() => handleDrawerOpen()}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon style={{ color: "white" }} />
            ) : (
              <ChevronRightIcon style={{ color: "white" }} />
            )}
          </IconButton>
        )}
      </div>
      <div className="drawer-container">
        <label className="drawer-title" style={{ color: "white" }}>
          Additional Application
        </label>
        <p className="drawer-subtitle" style={{ color: "white" }}>
          Please make sure all your personal information are correct and
          consistent with your government issued ID&apos;s.
        </p>
        <div className="drawer-item-list">
          <List>
            {[
              "Additional Information",
              "PEP Declaration",
              "FATCA",
              "Video Attestation",
              "Summary",
            ].map((item, index) => {
              const stepNavigation = index + 1;
              return (
                <ListItem
                  onClick={() =>
                    !isMaterializingUpload &&
                    account.eligible && !validation.loading && !modalAlert.modalSuccess &&
                    step.fixStep >= index + 1 &&
                    setStep((prevState) => ({
                      ...prevState,
                      step: stepNavigation,
                    }))
                  }
                  style={{
                    cursor: step.fixStep >= index + 1 ? "pointer" : "default",
                  }}
                  key={index}
                >
                  <ListItemIcon style={{ marginRight: "0" }}>
                    {step.fixStep > index + 1 ? (
                      <CheckCircleOutlineIcon color="primary" />
                    ) : (
                      <ChevronRightIcon style={{ color: "white" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    className={clsx(
                      "list-item-default",
                      step.fixStep > index + 1 ? "process-completed" : "",
                      step.step === index + 1 ? "process-current" : ""
                    )}
                  >
                    <span
                      className={`${
                        step.fixStep >= index + 1 && "sidebar-text"
                      }`}
                    >
                      {" "}
                      {item}{" "}
                    </span>
                  </ListItemText>
                </ListItem>
              );
            })}
          </List>
        </div>
      </div>
    </>
  );

  return (
      <div className={classes.root}>
        <CssBaseline />
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, classes.navBar)}
        >
          <Toolbar>
            <IconButton
              style={{ color: "#256141" }}
              aria-label="open drawer"
              onClick={() => handleDrawerOpen()}
              edge="start"
              className={clsx(classes.menuButton)}
            >
              <MenuIcon />
            </IconButton>
            <img className={classes.brand2} src={logo} alt="rampver-logo"></img>
          </Toolbar>
        </AppBar>

        <Hidden smDown implementation="css">
          <Drawer
            className={classes.drawer}
            variant="permanent"
            anchor="left"
            classes={{
              paper: classes.drawerPaper,
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden smUp implementation="css">
          <Drawer
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={validation.openDrawer}
            onClose={() => handleDrawerOpen()}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true,
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <div className={classes.mainContent}>
          <main className={clsx(classes.content)}>
            <div className={classes.drawerHeader} />
            <Container component="main">
              {renderSwitch(step.step)}
              {step.step !== 5 && (
                <SimpleModal
                  open={modalAlert.modalOpen}
                  success={modalAlert.modalSuccess}
                  title={modalAlert.modalTitle}
                  content={modalAlert.modalContent}
                  subText={modalAlert.modalSubText}
                  onClose={handleModalClose}
                />
              )}
            </Container>
          </main>
        </div>
      </div>
  );
};

const condition = (authUser) => !!authUser;

export default compose(withAuthorization(condition), withFirebase)(LongKYCPage);

export { LongKYCPage };
